import * as React from 'react';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { getLocalization } from 'global/global';
import { CampaignRow, GroupRow, UserRow } from './rows';

/**
 *  Column definitions for the three grids. Replaces the userFields /
 *  groupFields / campaignFields maps that DetailsView fed to jqxGrid.
 */

export interface ColumnOptions {
  /** Opens the row's form; the name cell is the link, as it was in jqxGrid. */
  onOpen: (id: number) => void;
}

/** dd/mm/yyyy, matching the legacy dateTimeCellRenderer. */
export const formatDate = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number) => `${n < 10 ? '0' : ''}${n}`;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

export const USER_ROLES = [
  { label: 'Admin user', value: 'admin,modeler' },
  { label: 'Modeler', value: 'modeler' },
  { label: 'Enumerator', value: 'enumerator' },
  { label: 'Task Assignee', value: 'taskAssignee' },
  { label: 'Viewer', value: 'viewer' },
  { label: 'Ordinary', value: 'ordinary' },
];

/** The name column is the link that opens the row's form, as it was in jqxGrid. */
const nameCell = <T extends { id: number }>(onOpen: (id: number) => void) => {
  const NameCell = (info: CellContext<T, unknown>) => (
    <a
      href={`#${info.row.original.id}`}
      onClick={(e) => {
        e.preventDefault();
        onOpen(info.row.original.id);
      }}
    >
      {info.getValue() as React.ReactNode}
    </a>
  );
  NameCell.displayName = 'NameCell';
  return NameCell;
};

export const userColumns = (options: ColumnOptions): ColumnDef<UserRow>[] => [
  {
    accessorKey: 'userName',
    header: () => getLocalization('userName'),
    cell: nameCell<UserRow>(options.onOpen),
    meta: { editable: 'text', width: 200 },
  },
  {
    accessorKey: 'active',
    header: () => getLocalization('active'),
    meta: { editable: 'checkbox', width: 80 },
  },
  {
    accessorKey: 'fullName',
    header: () => getLocalization('fullNames'),
    meta: { editable: 'text', width: 200 },
  },
  {
    accessorKey: 'email',
    header: () => getLocalization('email'),
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return value ? <a href={`mailto:${value}`}>{value}</a> : '';
    },
    meta: { width: 200 },
  },
  {
    accessorKey: 'roles',
    header: () => getLocalization('roles'),
    cell: ({ getValue }) =>
      USER_ROLES.find((r) => r.value === getValue())?.label || (getValue() as string),
    meta: { editable: 'select', options: USER_ROLES, width: 120 },
  },
  { accessorKey: 'groupName', header: () => getLocalization('group'), meta: { width: 200 } },
  {
    accessorKey: 'assignedLocs',
    header: () => getLocalization('assignedLocations'),
    meta: { width: 200 },
  },
  {
    accessorKey: 'lastLogin',
    header: () => getLocalization('recentLogin'),
    cell: ({ getValue }) => formatDate(getValue() as string | null),
    meta: { width: 200 },
  },
  {
    accessorKey: 'lastAppLogin',
    header: () => getLocalization('lastAppLogin'),
    cell: ({ getValue }) => formatDate(getValue() as string | null),
    meta: { width: 200 },
  },
];

export const groupColumns = (options: ColumnOptions): ColumnDef<GroupRow>[] => [
  {
    accessorKey: 'name',
    header: () => getLocalization('groupName'),
    cell: nameCell<GroupRow>(options.onOpen),
    meta: { editable: 'text', width: 200 },
  },
  {
    accessorKey: 'active',
    header: () => getLocalization('active'),
    meta: { editable: 'checkbox', width: 80 },
  },
  { accessorKey: 'formlist', header: () => getLocalization('formList'), meta: { width: 200 } },
  {
    accessorKey: 'hierarchy',
    header: () => getLocalization('locationHierarchy'),
    meta: { width: 200 },
  },
  {
    accessorKey: 'subgroups',
    header: () => getLocalization('subGroupNames'),
    meta: { width: 200 },
  },
  { accessorKey: 'users', header: () => getLocalization('numberOfUsers'), meta: { width: 200 } },
];

export const campaignColumns = (options: ColumnOptions): ColumnDef<CampaignRow>[] => [
  {
    accessorKey: 'name',
    header: () => getLocalization('campaignName'),
    cell: nameCell<CampaignRow>(options.onOpen),
    meta: { width: 200 },
  },
  {
    accessorKey: 'campaignTarget',
    header: () => getLocalization('campaignTarget'),
    meta: { width: 150 },
  },
  {
    accessorKey: 'campaignResponses',
    header: () => getLocalization('campaignResponses'),
    meta: { width: 150 },
  },
  {
    accessorKey: 'campaignStatus',
    header: () => getLocalization('campaignStatus'),
    meta: { width: 150 },
  },
  {
    accessorKey: 'campaignStartDate',
    header: () => getLocalization('campaignStartDate'),
    cell: ({ getValue }) => formatDate(getValue() as string | null),
    meta: { width: 150 },
  },
  {
    accessorKey: 'campaignEndDate',
    header: () => getLocalization('campaignEndDate'),
    cell: ({ getValue }) => formatDate(getValue() as string | null),
    meta: { width: 150 },
  },
  { accessorKey: 'formlist', header: () => getLocalization('formList'), meta: { width: 200 } },
  {
    accessorKey: 'hierarchy',
    header: () => getLocalization('locationHierarchy'),
    meta: { width: 200 },
  },
];
